import { useSelectiveFormData } from '@neo/blocks/forms/form-provider'
import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { usePlayAudio } from '@shared/hooks/use-play-audio'
import { Image, Music } from 'lucide-react'
import { useMemo, useState } from 'react'
import { v2validations } from 'v2/pages/flow/config/validation.config'
import { useGetLanguages, useGetProviders } from 'v2/pages/flow/knowledge-base/queries'
import { useTextToSpeechMutation } from 'v2/pages/flow/queries'
import { GenericSidebarComponent } from 'v2/pages/flow/sidebar/sidebar.base'
import type { RLanguage, RProviders } from 'v2/pages/flow/sidebar/types'
import type {
  AiSkillsNodeData,
  SidebarComponentProps,
  VoicePreviewHandler,
  VoicePreviewParams,
} from 'v2/pages/flow/types'
import { createVoicePreviewHandler } from 'v2/pages/flow/utils'
import { createDefaultConfigs, handleSubmit } from 'v2/pages/flow/utils/skill-node-utils'
import { getAudioTranscriptionFields } from './audio-transcription.config'
import { getImageAnalysisFields } from './image-analysis.config'
import { getImageGenerationFields } from './image-generation.config'
import { getTextSummarizationFields } from './text-summarization.config'
import { getTextToSpeechFields } from './text-to-speech.config'

interface AudioHandlers {
  isPlaying: (voiceId: string) => boolean
  handlePlayPreview: VoicePreviewHandler
  loadingVoiceId: string | null
  stop: () => void
}

export type FormValues = AiSkillsNodeData['config']

const ToolOption = ({ icon, label }: { icon: React.ReactNode; label: string }) => (
  <div className="flex gap-2 items-center">
    {icon}
    {label}
  </div>
)

const fields = (
  nodeVars: string[],
  providers?: RProviders,
  languages?: RLanguage[],
  audioHandlers?: AudioHandlers,
): GenericFieldConfig<FormValues> => {
  const validations = v2validations({ nodeVars }).ai_skills
  const toolTypes = [
    {
      label: 'Image',
      options: [
        {
          label: (
            <ToolOption
              icon={<Image className="text-brand-text w-4 h-4" />}
              label="Generate Image"
            />
          ),
          value: 'image_generation',
          searchValue: 'Generate Image',
        },
        {
          label: (
            <ToolOption
              icon={<Image className="text-brand-text w-4 h-4" />}
              label="Analyze Image"
            />
          ),
          value: 'image_analysis',
          searchValue: 'Analyze Image',
        },
      ],
    },
    {
      label: 'Audio',
      options: [
        {
          label: (
            <ToolOption
              icon={<Music className="text-brand-text w-4 h-4" />}
              label="Transcribe Audio"
            />
          ),
          value: 'audio_transcription',
          searchValue: 'Transcribe Audio',
        },
        {
          label: (
            <ToolOption
              icon={<Music className="text-brand-text w-4 h-4" />}
              label="Generate Audio"
            />
          ),
          value: 'text_to_speech',
          searchValue: 'Generate Audio',
        },
      ],
    },
    // TODO: Add text summarization when BE is ready
    // {
    //   label: 'Text',
    //   options: [{ label: 'Text Summarization', value: 'summary' }],
    // },
  ]

  const baseFields = [
    {
      name: 'name',
      label: 'Name',
      zodValidation: validations.shape.name,
      componentType: 'input',
    },
    {
      name: 'tool_type',
      label: 'Skill Type',
      zodValidation: validations.shape.tool_type,
      componentType: 'selectWithGroups',
      inputProps: {
        placeholder: 'Select skill type',
        label: 'Skill Type',
        groups: toolTypes,
        searchValue: 'label',
      },
    },
  ] as GenericFieldConfig<FormValues>

  return [
    ...baseFields,
    ...getImageGenerationFields(nodeVars, providers),
    ...getImageAnalysisFields(nodeVars, providers),
    ...getAudioTranscriptionFields(nodeVars, providers, languages),
    ...getTextToSpeechFields(nodeVars, providers, audioHandlers),
    ...getTextSummarizationFields(nodeVars, providers),
  ]
}

export const AiSkillsNodeSidebarComponent = (props: SidebarComponentProps<AiSkillsNodeData>) => {
  const { nodeVars, selectedNode } = props
  const language = useSelectiveFormData<FormValues, string>(
    selectedNode.type || '',
    // biome-ignore lint/suspicious/noExplicitAny: TODO - fix type definition
    (state: any) => state.audio_transcription_config?.language || '',
  )

  const { data: providers } = useGetProviders(language)
  const { data: languages } = useGetLanguages()
  const { isPlaying, play, stop } = usePlayAudio()
  const textToSpeechMutation = useTextToSpeechMutation()
  const [loadingVoiceId, setLoadingVoiceId] = useState<string | null>(null)

  const handlePlayPreview = useMemo(
    () => async (params: VoicePreviewParams) => {
      try {
        setLoadingVoiceId(params.voiceId)
        await createVoicePreviewHandler(play, textToSpeechMutation)(params)
      } finally {
        setLoadingVoiceId(null)
      }
    },
    [play, textToSpeechMutation],
  )

  const audioHandlers = useMemo(
    () => ({ isPlaying, handlePlayPreview, loadingVoiceId, stop }),
    [isPlaying, handlePlayPreview, loadingVoiceId, stop],
  )

  const formFields = useMemo(
    () => fields(nodeVars, providers?.data, languages?.data?.languages || [], audioHandlers),
    [nodeVars, providers?.data, languages?.data?.languages, audioHandlers],
  )

  // Use createDefaultConfigs but prevent form resets by not depending on providers changes
  const defaultValues = useMemo(
    () => createDefaultConfigs(selectedNode, providers?.data),
    [selectedNode.id, selectedNode.data.config], // Disabled providers?.data dependency to prevent form resets when providers change
  )

  return (
    <GenericSidebarComponent
      defaultValues={defaultValues}
      fields={formFields}
      callback={handleSubmit}
      {...props}
    />
  )
}
